import '../styles/reset.css';
import 'intersection-observer'; // polyfill for IntersectionObserver
import Cookie from 'js-cookie';
import Cookies from 'cookies';
import getConfig from 'next/config';
import i18n from 'localization/i18n';
import smoothscroll from 'smoothscroll-polyfill';
import store from 'store';
import { AppProps } from 'next/app';
import { I18nextProvider } from 'react-i18next';
import { Provider } from 'react-redux';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import BreadcrumbsProvider from 'breadcrumbs/providers/BreadcrumbProvider';
import BugsnagProvider from 'bugsnag/providers/BugsnagProvider';
import FeatureFlagContextProvider from 'featureFlags/providers/FeatureFlagsProvider';
import { GlobalMetadata } from 'layout/head/GlobalMetadata';
import { AccountModalProvider } from 'account/providers/AccountModalProvider';
import { AuthProvider } from 'auth/providers/AuthProvider';
import { BannerProvider } from 'banner/providers/BannerProvider';
import { BranchSdkProvider } from 'banner/providers/BranchSdkProvider';
import { ConstructorNodeClient } from 'search/utils/ConstructorNodeClient';
import { ConstructorProvider } from 'search/providers/ConstructorProvider';
import { CountriesProvider } from 'shared/providers/CountriesProvider';
import { customCache } from 'homepage/utils/HomeTabCustomCache';
import { DEFAULT_ITEMS_COUNT } from 'layout/header/enums/constants';
import { GLOBAL_PRICING_ID, GLOBAL_PRICING_REGIONS } from 'shared/enums/GlobalPricing';
import { SWRConfig } from 'swr';
import {
  GoogleAWTagScript,
  ImpactScript,
  GoogleTagScript,
  SnapchatScript,
} from 'tracking/components';
import { FeatureFlags } from 'featureFlags/constants';
import { homepageDataCache } from 'homepage/utils/HomepageDataAndPositionCache';
import { OneTrustScript } from 'tracking/components/OneTrustScript';
import { isSearchCrawler } from 'shared/utils/isSearchCrawler';
import { requestService } from 'shared/utils/services/requestService';
import { ICMSBreadcrumb } from 'shared/types/Breadcrumbs';
import { ICmsBanner } from 'banner/types/cmsBanner';
import { IUser } from 'auth/types/User';
import {
  MParticleProvider,
  GoogleTagProvider,
  ImpactProvider,
  SnapchatProvider,
} from 'tracking/providers';
import { PageLayout } from 'layout/PageLayout';
import { PageLayoutProvider } from 'layout/providers/PageLayoutProvider';
import { getBugsnagApiKey, startBugsnag } from 'bugsnag/utils';
import { STORAGE } from 'shared/enums/SitePreferences';
import {
  createCalendarBreadcrumbStructure,
  createNavigationBreadcrumbStructure,
  transformBreadcrumbs,
} from 'server/utils/breadcrumbs/transformBreadcrumbs';
import { bugsnag } from 'shared/utils/services/bugsnag';
import { EXPERIMENT_STORE } from 'experiments/utils/constants';
import { formatExperimentsList } from 'experiments/utils';
import { getConstructorNodeClientUserParams } from 'search/utils/getConstructorNodeClientUserParams';
import { getUtmData } from 'shared/utils/getUtmData';
import { IExperiment } from 'experiments/utils/types';
import { PubNubProvider } from 'community/providers/PubNubProvider';
import { sendTrackingEvent } from 'tracking/utils';
import { startSession } from 'shared/services/startSession';
import { TiktokScript } from 'tracking/components/TiktokScript';
import { SECURE_ROUTES } from 'shared/enums/SecureRoutes';

interface IFeatureFlags {
  [key: string]: boolean;
}
interface IAppProps extends AppProps {
  breadcrumbs: ICMSBreadcrumb[];
  cmsBanner: ICmsBanner;
  cspNonce: string;
  featureFlags: IFeatureFlags;
  headerItemsCount: number;
  isCrawler: boolean;
  namespace: string | undefined;
  siteCohortCookieValue: number;
  referrer: string;
  user: IUser;
  isAutomation: boolean;
  activeExperiments: Array<IExperiment>;
}

// TODO: probably move this elsewhere. Especially if it grows more complex
const swrFetcher = (url) => requestService({ url });

const App = ({
  breadcrumbs,
  cmsBanner,
  Component,
  cspNonce,
  featureFlags,
  headerItemsCount,
  isAutomation,
  isCrawler,
  namespace,
  pageProps,
  referrer,
  user,
}: IAppProps) => {
  const router = useRouter();
  const { query, asPath } = router;
  const isAuction = asPath?.includes('auctions');
  const isInApp = query?.app === 'y';
  const utmData = getUtmData(query, referrer);
  const [bugsnagStarted, setBugsnagStarted] = useState(false);
  const {
    publicRuntimeConfig: { RELEASE_ENV },
  } = getConfig();
  const isNonDev = RELEASE_ENV !== 'development';
  const isImpactEnabled = !!featureFlags?.[FeatureFlags.WEB_ENABLE_IMPACT];
  const isOneTrustEnabled = !!featureFlags?.[FeatureFlags.WEB_ENABLE_ONE_TRUST];
  const isSnapchatEnabled = !!featureFlags?.[FeatureFlags.WEB_ENABLE_SNAPCHAT];
  const isTiktokEnabled = !!featureFlags?.[FeatureFlags.WEB_ENABLE_TIKTOK];

  useEffect(() => {
    if (featureFlags?.tempWebEnableCloudflareCacheHomepage) {
      const isSessionSecure = Cookie.get('csrf');
      /**
       * checks if the global middleware have been called
       * if not, we know we're viewing a cache version of the current page
       * and should start the session
       */
      if (!isSessionSecure) {
        void startSession();
      }
    }

    if (window) {
      smoothscroll.polyfill();
    }
    // tracks when GOAT.com session begins from a user clicking on a campaign link
    if (utmData && bugsnagStarted) {
      sendTrackingEvent('CAMPAIGN_SESSION_START', utmData);
    }
  }, [featureFlags, utmData, bugsnagStarted]);

  useEffect(() => {
    try {
      window?.mParticle.logPageView(
        'PageView',
        {
          hostname: window?.location.hostname,
          title: document?.title,
        },
        {
          'Google.Label': asPath,
          'Google.Page': asPath,
          'Google.Title': document?.title,
        },
      );
    } catch (error) {
      bugsnag(error, { context: 'Error logging page view to mParticle for Google' });
    }

    const storage = global.sessionStorage;
    if (!storage) return;

    const prevPath = storage.getItem('currentPath');
    storage.setItem('prevPath', prevPath);
    storage.setItem('currentPath', asPath);

    // TODO: this is initialized multiple times
    customCache.initialize();
    homepageDataCache.initialize();
  }, [asPath]);

  if (getBugsnagApiKey() && !bugsnagStarted) {
    void startBugsnag();
    void setBugsnagStarted(true);
  }

  return (
    <Provider store={store}>
      <BugsnagProvider>
        <GlobalMetadata />
        {isNonDev && !isCrawler && (
          <>
            {isOneTrustEnabled && !isAutomation && !isAuction && !isInApp && (
              <OneTrustScript cspNonce={cspNonce} />
            )}
            <GoogleAWTagScript isOneTrustEnabled={isOneTrustEnabled} cspNonce={cspNonce} />
            <GoogleTagScript isOneTrustEnabled={isOneTrustEnabled} cspNonce={cspNonce} />
            {isImpactEnabled && (
              <ImpactScript isOneTrustEnabled={isOneTrustEnabled} cspNonce={cspNonce} />
            )}
            {isTiktokEnabled && (
              <TiktokScript isOneTrustEnabled={isOneTrustEnabled} cspNonce={cspNonce} />
            )}
          </>
        )}
        <SWRConfig
          value={{
            fetcher: swrFetcher,
          }}
        >
          <BreadcrumbsProvider breadcrumbs={breadcrumbs}>
            <I18nextProvider i18n={i18n}>
              <AuthProvider user={user}>
                {/* NOTE: Snapchat script located here to get access to auth provider */}
                {isSnapchatEnabled && isNonDev && !isCrawler && (
                  <SnapchatScript isOneTrustEnabled={isOneTrustEnabled} cspNonce={cspNonce} />
                )}
                <FeatureFlagContextProvider featureFlags={featureFlags}>
                  <PubNubProvider namespace={namespace}>
                    <MParticleProvider>
                      <GoogleTagProvider>
                        <ImpactProvider>
                          <SnapchatProvider>
                            <ConstructorProvider>
                              <PageLayoutProvider>
                                <AccountModalProvider>
                                  <BannerProvider cmsBanner={cmsBanner}>
                                    <BranchSdkProvider cspNonce={cspNonce}>
                                      <CountriesProvider>
                                        <PageLayout
                                          headerItemsCount={headerItemsCount}
                                          isCrawler={isCrawler}
                                        >
                                          <Component {...pageProps} />
                                        </PageLayout>
                                      </CountriesProvider>
                                    </BranchSdkProvider>
                                  </BannerProvider>
                                </AccountModalProvider>
                              </PageLayoutProvider>
                            </ConstructorProvider>
                          </SnapchatProvider>
                        </ImpactProvider>
                      </GoogleTagProvider>
                    </MParticleProvider>
                  </PubNubProvider>
                </FeatureFlagContextProvider>
              </AuthProvider>
            </I18nextProvider>
          </BreadcrumbsProvider>
        </SWRConfig>
      </BugsnagProvider>
    </Provider>
  );
};

App.getInitialProps = async ({ Component, ctx }) => {
  let breadcrumbs = {};
  let cmsBanner = {};
  let cspNonce = '';
  let featureFlags = { webEnableGoatBanner: false, webEnableBranchSdkBanner: false };
  let headerItemsCount = DEFAULT_ITEMS_COUNT;
  let namespace;
  let pageProps = {};
  let referrer = null;
  let user;
  let isAutomation = false;
  let isCrawler = false;
  let activeExperiments;

  // TODO: fetch data on client side, if not provided by server.
  if (ctx.res?.locals?.user) {
    user = {
      ...ctx.res.locals.user,
    };
  }
  if (ctx.res?.locals?.cspNonce) {
    cspNonce = ctx.res.locals.cspNonce;
  }

  // Keep this on top to prevent unused api calls
  if (SECURE_ROUTES.has(ctx.pathname) && (!user || Object.keys(user).length === 0)) {
    ctx.res.writeHead(302, { Location: `/login?redirect=${ctx.asPath as string}` });
    ctx.res.end();
    return {};
  }

  if (ctx.req) {
    // server side rendered data
    featureFlags = ctx.req.featureFlags;
    breadcrumbs = ctx.req.breadcrumbs;
    cmsBanner = ctx.req.cmsBanner?.sections?.[0];
    referrer = ctx.req.headers.referer;
    isAutomation = ctx.req.cookies?.is_goat_automation === 'true';
    isCrawler = isSearchCrawler(ctx.req.headers?.['user-agent']);
    activeExperiments = ctx.req.cookies?.[EXPERIMENT_STORE.ACTIVE_EXPERIMENTS];
    namespace = ctx.req.headers?.['x-goat-namespace'];
    const constructorUserParams = getConstructorNodeClientUserParams(ctx.req);

    // Fetching all constructor response in a synchronous PROMISE
    // This allows us to:
    // 1. decrease fetching time
    // 2. confirm & catch that the error is from the internal constructor api itself
    // not from this code
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const [browseGroupsResponse, lowestPriceCentsResponse]: any[] = await Promise.all([
        new Promise((resolve) => {
          ConstructorNodeClient.browse
            .getBrowseGroups(
              {
                filters: {
                  group_id: 'all',
                },
              },
              {},
              constructorUserParams,
            )
            .then((res) => {
              if (typeof res === 'object') {
                resolve(res);
              } else {
                bugsnag('Constructor response malformed', {
                  context: 'Constructor response malformed',
                });
                resolve(null);
              }
            })
            .catch((error) => {
              if (typeof error !== 'undefined') {
                bugsnag(error, { context: 'Fetching Items count For Nav' });
              }
              resolve(null);
            });
        }),
        new Promise((resolve) => {
          ConstructorNodeClient.browse
            .getBrowseFacetOptions('lowest_price_cents', {}, constructorUserParams)
            .then((res) => {
              if (typeof res === 'object') {
                resolve(res);
              } else {
                bugsnag('Constructor malformed response', {
                  context: 'Constructor malformed response',
                });
                resolve(null);
              }
            })
            .catch((error) => {
              if (typeof error !== 'undefined') {
                bugsnag(error, { context: 'Fetching Global Pricing regions Map' });
              }
              resolve(null);
            });
        }),
      ]);

      if (browseGroupsResponse) {
        headerItemsCount = browseGroupsResponse?.response?.groups?.[0]?.count;
      }

      if (lowestPriceCentsResponse) {
        const cookies = new Cookies(ctx.req, ctx.res);
        const country = ctx.res?.locals?.country || ctx.req?.cookies?.[STORAGE.COUNTRY];
        const globalPricingMap =
          lowestPriceCentsResponse?.response?.facets?.[0]?.data?.global_pricing_regions;
        const globalPricingId = globalPricingMap?.[country] ?? null;
        cookies.set(GLOBAL_PRICING_REGIONS, JSON.stringify(globalPricingMap), {
          httpOnly: false,
          sameSite: 'strict',
        });
        cookies.set(GLOBAL_PRICING_ID, globalPricingId, {
          httpOnly: false,
          sameSite: 'strict',
        });
      }
    } catch (error) {
      if (typeof error !== 'undefined') {
        bugsnag(error, { context: 'Fetching responses from ConstructorIO api' });
      }
    }

    // TODO: need backend to allow instantiation of a user and return a unique id for it
    // as of now to be able to A/B Test without the user experiencing flash of content
    // we need to get a clientId (=== mparticle device id) and assign groups to the current clients and then work with sessions
    // in order to log exposures accurately

    if (ctx.req?.featureFlags?.webAllowExperiments) {
      try {
        const cookies = new Cookies(ctx.req, ctx.res);
        // fetch fresh active experiments from Darwin service
        // unfortunately for security purpose, there is no protocol passed
        // in NextRequest or NextResponse in getInitialProps
        // we have to rely on the env variables
        const protocol = process.env.NODE_ENV === 'development' ? 'http' : 'https';
        const currentReferer = `${protocol}://${ctx.req.headers.host as string}`;

        const experimentResponse = await fetch(`${currentReferer}/web-api/darwin/list-experiments`)
          .then((res) => {
            return res.json();
          })
          .catch((error) => {
            if (typeof error !== 'undefined') {
              bugsnag(error, { context: 'Fetching list of experiments' });
            }
            return null;
          });
        // format the list of experiments to remove unwanted properties
        // set it in browser cookies
        // only if the server comes back with some responses
        if (experimentResponse) {
          activeExperiments = formatExperimentsList(experimentResponse?.experimentsList);
          cookies.set(EXPERIMENT_STORE.ACTIVE_EXPERIMENTS, JSON.stringify(activeExperiments), {
            httpOnly: false,
            sameSite: 'strict',
          });
        } else {
          cookies.set(EXPERIMENT_STORE.ACTIVE_EXPERIMENTS);
        }
      } catch (error) {
        if (typeof error !== 'undefined') {
          bugsnag(error, { context: 'Fetching list of experiments' });
        }
      }
    }
  } else {
    try {
      const response = await fetch('/web-api/featureFlags');
      featureFlags = await response.json();
    } catch (error) {
      if (typeof error !== 'undefined') {
        bugsnag(error, { context: 'Fetching Feature Flags' });
      }
    }

    try {
      const response = await fetch('/web-api/v1/breadcrumbs');
      const responseJSON = await response.json();
      const transformedCrumbs = transformBreadcrumbs(responseJSON);
      const navigationCrumbs = createNavigationBreadcrumbStructure(transformedCrumbs);
      const calendarCrumbs = createCalendarBreadcrumbStructure(transformedCrumbs);
      breadcrumbs = {
        ...transformedCrumbs,
        calendar: calendarCrumbs,
        navigation: navigationCrumbs,
      };
    } catch (error) {
      if (typeof error !== 'undefined') {
        bugsnag(error, { context: 'Fetching Breadcrumbs' });
      }
    }

    if (featureFlags?.webEnableGoatBanner) {
      try {
        const response = await fetch('/web-api/v1/cms/cms_articles/web-cta-banner');
        const responseJSON = await response.json();
        cmsBanner = responseJSON?.sections?.[0];
      } catch (error) {
        if (typeof error !== 'undefined') {
          bugsnag(error, { context: 'Fetching Web CTA Banner' });
        }
      }
    }
  }

  if (Component.getInitialProps) {
    pageProps = await Component.getInitialProps(ctx);
  }

  return {
    breadcrumbs,
    cmsBanner,
    cspNonce,
    featureFlags,
    headerItemsCount,
    isAutomation,
    isCrawler,
    namespace,
    pageProps,
    referrer,
    user,
  };
};

export default App;
